import type { PlacesSearchResponse, PlaceResult } from '@poi-planner/shared';
import { api } from './api';

/**
 * Patterns to detect Google Maps URLs
 */
const GOOGLE_MAPS_URL_PATTERNS = [
  /^https?:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps)\//,
  /^https?:\/\/(www\.)?google\.(com|[a-z]{2,3}(\.[a-z]{2})?)\/maps\//,
];

/**
 * Check if input looks like a Google Maps URL
 */
export function isGoogleMapsUrl(input: string): boolean {
  return GOOGLE_MAPS_URL_PATTERNS.some(pattern => pattern.test(input.trim()));
}

/**
 * Google Places search service.
 * Uses server-side proxy to keep API key secure.
 */

export class PlacesApiError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'PlacesApiError';
  }
}

/**
 * Search for places via server proxy.
 * Server handles Google Places API calls with secure key.
 */
export async function searchPlaces(query: string): Promise<PlacesSearchResponse> {
  if (!query || !query.trim()) {
    return { results: [] };
  }

  try {
    return await api.searchPlaces(query.trim());
  } catch (error: any) {
    if (error.status === 503) {
      throw new PlacesApiError('Places search not configured', 503);
    }
    throw new PlacesApiError(
      error.message || 'Failed to search places',
      error.status
    );
  }
}

/**
 * Resolve a Google Maps URL to place data.
 * Handles shortened URLs (maps.app.goo.gl) and full URLs.
 */
export async function resolvePlaceUrl(url: string): Promise<PlaceResult | null> {
  try {
    const response = await api.resolvePlaceUrl(url);
    if (response.error) {
      throw new PlacesApiError(response.error);
    }
    return response.result;
  } catch (error: any) {
    if (error instanceof PlacesApiError) {
      throw error;
    }
    throw new PlacesApiError(
      error.message || 'Failed to resolve URL',
      error.status
    );
  }
}
